import { filterClasses } from "@swivl-mono/helpers";
import type { DebugLevel } from "@swivl-mono/types";
import { IoBuildOutline } from "react-icons/io5";

import type { ChatMessage } from "../Types/BotChat.Types";
import { HumanizedData } from "./HumanizedData";
import { InvisibleRow, SECTION_LABEL } from "./InvisibleRow";
import { StructuredMessage } from "./Structured/StructuredMessage";

/** The context fields we surface, in a fixed order, skipping any that are unset. */
function contextEntries(context: Record<string, any>): [string, string][] {
  return (["location", "unit", "lease", "channel"] as const)
    .map((key): [string, string] => [key, context[key === "channel" ? "channel" : `${key}Id`]])
    .filter((entry): entry is [string, string] => Boolean(entry[1]));
}

/**
 * Tools whose output IS a structured-message payload — the expand renders the
 * real card (exactly what the customer was shown) instead of key/value rows.
 * `canRender` guards the shape so malformed/legacy rows fall back to
 * {@link HumanizedData} rather than crashing the timeline.
 *
 * Pickers (`find_locations`, `list_units`) are deliberately absent: the
 * customer-visible picker card already renders as its own timeline message
 * right after the tool row, and the carousel doesn't fit the expand area —
 * their outputs read better as humanized rows.
 */
const TOOL_OUTPUT_KIND: Record<string, { kind: string; canRender: (output: any) => boolean }> = {
  emit_buttons: { kind: "buttons", canRender: (output) => Array.isArray(output?.options) },
  emit_profile_form: { kind: "profile-form", canRender: (output) => Array.isArray(output?.fields) }
};

interface ToolCallCardProps {
  content: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  /**
   * Viewer's resolved level — forwarded to structured output renderers
   * (Buttons surfaces "→ LLM saw" lines at `agent`+). Defaults to "agent":
   * tool calls are admin-only, so any viewer of this card is at least that.
   */
  debugLevel?: DebugLevel;
}

/**
 * A tool the bot invoked — ADMIN-ONLY (Chuck, 2026-08-18): operator agents
 * never see Swivl's machinery; the classifier keeps these rows off their wire
 * and the timeline gates the render. Collapsed to the shared
 * {@link InvisibleRow} pill, with the tool's name, a success dot and its
 * latency in the label so the row is readable without expanding; input,
 * output, error and context sit behind the expand.
 *
 * The expand is the human-readable record of the call — humanized rows, never
 * raw JSON. Sections render on field *presence*, not truthiness: an output of
 * `false`/`""`/`null` is information ("the tool returned nothing") and shows
 * as an explicit "—" rather than vanishing.
 */
export function ToolCallCard({ content, metadata, createdAt, debugLevel = "agent" }: ToolCallCardProps): React.ReactElement {
  const meta = metadata || {};
  const hasDetail = "input" in meta || "output" in meta || Boolean(meta.error) || Boolean(meta.context);

  const outputEntry = meta.success !== false ? TOOL_OUTPUT_KIND[meta.toolName] : undefined;
  const structuredOutput = outputEntry?.canRender(meta.output) ? outputEntry : undefined;
  // The record of what the tool emitted: the full offered set, unselected —
  // no `actionTaken`, since the customer's pick lives on the visible message.
  const syntheticOutputMessage: ChatMessage | undefined = structuredOutput
    ? {
        messageId: `${meta.toolUseId ?? "tool"}-output`,
        messageIndex: -1,
        role: "assistant",
        content: "",
        createdAt: createdAt ?? "",
        contentType: "structured",
        payload: { kind: structuredOutput.kind, data: meta.output }
      }
    : undefined;

  const label = (
    <>
      {meta.toolName || "Unknown tool"}
      <span
        className={filterClasses("mx-1.5 inline-block size-1.5 rounded-full align-middle", meta.success !== false ? "bg-teal-500" : "bg-red-500")}
      />
      {meta.latencyMs != null ? `${meta.latencyMs}ms` : ""}
    </>
  );

  return (
    <InvisibleRow icon={<IoBuildOutline size={16} />} label={label} createdAt={createdAt} tone="purple">
      {hasDetail ? (
        <>
          {"input" in meta && (
            <div className="mb-2">
              <p className={SECTION_LABEL}>Input</p>
              <HumanizedData data={meta.input} />
            </div>
          )}
          {"output" in meta && (
            <div className="mb-2">
              <p className={SECTION_LABEL}>Output</p>
              {syntheticOutputMessage ? (
                // Centered under the pill; Buttons' own row alignment would
                // right-align inside the centered expand otherwise.
                <div className="flex justify-center">
                  <StructuredMessage message={syntheticOutputMessage} readOnly debugLevel={debugLevel} />
                </div>
              ) : (
                <HumanizedData data={meta.output} />
              )}
            </div>
          )}
          {meta.error && (
            <div className="mb-2">
              <p className={SECTION_LABEL}>Error</p>
              {/* Tinted red — the message is machine output, not prose. */}
              {/* `wrap-break-word` because these are usually raw API payloads:
                  one unbroken run of JSON with no spaces to wrap at, which
                  would otherwise run past the edge of the box. */}
              <p className="body-mono mt-1 rounded-sm border border-red-200 bg-red-50 p-2 text-left wrap-break-word whitespace-pre-wrap text-red-700">
                {meta.error}
              </p>
            </div>
          )}
          {meta.context && (
            <div>
              <p className={SECTION_LABEL}>Context</p>
              <div className="mt-1 flex flex-wrap justify-center gap-1">
                {contextEntries(meta.context).map(([key, value]) => (
                  <span key={key} className="body-mono rounded-sm bg-grey-50 px-2 py-1 text-grey-600">
                    {key}: {value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : undefined}
    </InvisibleRow>
  );
}
